import { request } from './base.service';

export interface GmailStatus {
    connected: boolean;
    email: string;
    watchExpiry: string | null;
    draftCount: number;
}

export interface ImportDraft {
    id: string;
    userId: string;
    connectionId: string;
    gmailMessageId: string;
    source: string;
    title: string;
    amount: number;
    type: 'DEBIT' | 'CREDIT';
    date: string;
    category: string | null;
    confidence: number;
    rawSnippet: string | null;
    rawBody?: string | null;
    rawBodyFormat?: 'html' | 'text' | null;
    tag?: string | null;
    senderDomain?: string | null;
    parsedMerchant?: string | null;
    parsedAccount?: string | null;
    parsedAmount?: number | null;
    parsedDate?: string | null;
    status: 'pending' | 'accepted' | 'dismissed';
    listId: string;
}

// ── Gmail connection ──

export const getGmailAuthUrl = (userid: string): Promise<{ url: string }> =>
    request(`/gmail/auth-url?userid=${encodeURIComponent(userid)}`);

export const getGmailStatus = (userid: string): Promise<GmailStatus> =>
    request(`/gmail/status?userid=${encodeURIComponent(userid)}`);

export const disconnectGmail = (userid: string): Promise<void> =>
    request(`/gmail/disconnect?userid=${encodeURIComponent(userid)}`, { method: 'DELETE' });

export const syncGmail = (userid: string): Promise<{ synced: number }> =>
    request(`/gmail/sync?userid=${encodeURIComponent(userid)}`, { method: 'POST' });

// ── Import drafts ──

export const getDrafts = (userid: string, status?: string): Promise<{ drafts: ImportDraft[] }> => {
    const params = new URLSearchParams({ userid });
    if (status) params.set('status', status);
    return request(`/imports?${params.toString()}`);
};

export const editDraft = (
    id: string,
    updates: Partial<Pick<ImportDraft, 'title' | 'amount' | 'type' | 'date' | 'category' | 'tag'>>,
): Promise<{ draft: ImportDraft }> =>
    request(`/imports/${encodeURIComponent(id)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
    });

export const acceptDraft = (id: string, listid: string): Promise<{ draft: ImportDraft }> =>
    request(`/imports/${encodeURIComponent(id)}/accept`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ listid }),
    });

export const dismissDraft = (id: string): Promise<{ draft: ImportDraft }> =>
    request(`/imports/${encodeURIComponent(id)}/dismiss`, { method: 'PUT' });

export const bulkAcceptDrafts = (ids: string[], listid: string): Promise<{ accepted: number }> =>
    request('/imports/bulk-accept', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids, listid }),
    });

export const getDraftBody = (id: string): Promise<{ body: string; format: 'html' | 'text' }> =>
    request(`/imports/${encodeURIComponent(id)}/body`);

export const renewGmailWatches = (userid: string): Promise<{ renewed: number }> =>
    request(`/gmail/renew-watches?userid=${encodeURIComponent(userid)}`, { method: 'POST' });

// ── Blocked senders ──

export interface BlockedSender {
    id: string;
    userId: string;
    senderDomain: string;
    reason: string | null;
    createdAt: string;
}

export const getBlockedSenders = (userid: string): Promise<{ blockedSenders: BlockedSender[] }> =>
    request(`/blocked-senders?userid=${encodeURIComponent(userid)}`);

export const blockSender = (userid: string, senderDomain: string, reason?: string): Promise<{ blockedSender: BlockedSender }> =>
    request('/blocked-senders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userid, senderDomain, reason: reason || null }),
    });

export const unblockSender = (id: string): Promise<void> =>
    request(`/blocked-senders/${encodeURIComponent(id)}`, { method: 'DELETE' });
